import { useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Check, X, ChevronDown, User, Users } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks/use-mobile';
import { useMarkBedCountEntry, type BedCountEntry, type BedCountResult } from '@/hooks/useBedCount';

interface FamilyUnitRowProps {
  /** Head-of-household entry (if present) — drives the cascading mark. */
  hohEntry: BedCountEntry;
  /** Member entries belonging to the same family unit. */
  memberEntries: BedCountEntry[];
  clientById: Map<string, any>;
  roomById: Map<string, any>;
  getClientPhoto?: (id: string) => string | undefined;
}

/**
 * One row per family unit (FwC protocol). Marking the HOH cascades the same
 * status to all members. Expand to mark members individually.
 */
export const FamilyUnitRow = ({
  hohEntry,
  memberEntries,
  clientById,
  roomById,
  getClientPhoto,
}: FamilyUnitRowProps) => {
  const isMobile = useIsMobile();
  const markMut = useMarkBedCountEntry();
  const [expanded, setExpanded] = useState(false);

  const hohClient = clientById.get(hohEntry.client_id);
  const room = roomById.get(hohEntry.room_id ?? '');
  const fullName = hohClient
    ? `${hohClient.first_name ?? ''} ${hohClient.last_name ?? ''}`.trim() || hohClient.name?.trim() || 'Unknown'
    : (hohClient?.name?.trim() || 'Unknown');

  const allEntries = useMemo(() => [hohEntry, ...memberEntries], [hohEntry, memberEntries]);
  const isPass = hohEntry.expected_status === 'on_pass';

  const cascade = (result: BedCountResult) => {
    allEntries.forEach((e) => {
      if (e.result !== result) {
        markMut.mutate({ entryId: e.id, result });
      }
    });
  };

  const allMatch = (result: BedCountResult) =>
    allEntries.every((e) => e.result === result);

  const renderActions = (target: BedCountEntry, label?: boolean) => {
    const isHoH = target.id === hohEntry.id;
    const onPass = target.expected_status === 'on_pass';
    return (
      <div className={cn('flex gap-2', isMobile && 'w-full')}>
        {onPass ? (
          <Button
            size={isMobile ? 'lg' : 'sm'}
            variant={target.result === 'pass_acknowledged' ? 'default' : 'outline'}
            onClick={() =>
              isHoH ? cascade('pass_acknowledged') : markMut.mutate({ entryId: target.id, result: 'pass_acknowledged' })
            }
            className={cn(isMobile && 'flex-1 min-h-[56px]')}
          >
            <Check className="h-4 w-4" /> Acknowledge
          </Button>
        ) : (
          <>
            <Button
              size={isMobile ? 'lg' : 'sm'}
              variant={(isHoH ? allMatch('present') : target.result === 'present') ? 'default' : 'outline'}
              onClick={() => isHoH ? cascade('present') : markMut.mutate({ entryId: target.id, result: 'present' })}
              className={cn(isMobile && 'flex-1 min-h-[56px]')}
            >
              <Check className="h-4 w-4" /> Present
            </Button>
            <Button
              size={isMobile ? 'lg' : 'sm'}
              variant={(isHoH ? allMatch('not_present') : target.result === 'not_present') ? 'destructive' : 'outline'}
              onClick={() => isHoH ? cascade('not_present') : markMut.mutate({ entryId: target.id, result: 'not_present' })}
              className={cn(isMobile && 'flex-1 min-h-[56px]')}
            >
              <X className="h-4 w-4" /> Not Present
            </Button>
            <Button
              size={isMobile ? 'lg' : 'sm'}
              variant={(isHoH ? allMatch('unbedded') : target.result === 'unbedded') ? 'secondary' : 'outline'}
              onClick={() => isHoH ? cascade('unbedded') : markMut.mutate({ entryId: target.id, result: 'unbedded' })}
              className={cn(isMobile && 'flex-1 min-h-[56px]')}
            >
              Unbed
            </Button>
          </>
        )}
      </div>
    );
  };

  const overall: BedCountResult | null =
    allMatch('present') ? 'present' :
    allMatch('not_present') ? 'not_present' :
    allMatch('unbedded') ? 'unbedded' :
    allMatch('pass_acknowledged') ? 'pass_acknowledged' :
    null;

  return (
    <div
      className={cn(
        'border rounded-lg p-3 transition-colors',
        overall === 'present' && 'bg-primary/5 border-primary/30',
        overall === 'not_present' && 'bg-destructive/5 border-destructive/30',
        overall === 'unbedded' && 'bg-muted',
        overall === 'pass_acknowledged' && 'bg-secondary/40'
      )}
    >
      <div className={cn('flex gap-3', isMobile ? 'flex-col' : 'items-center')}>
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <Avatar className="h-10 w-10 shrink-0">
            <AvatarImage src={getClientPhoto?.(hohEntry.client_id) ?? undefined} alt={fullName} />
            <AvatarFallback><User className="h-5 w-5" /></AvatarFallback>
          </Avatar>
          <div className="min-w-0 flex-1">
            <div className="font-medium truncate flex items-center gap-2">
              {fullName}
              <Badge variant="outline" className="gap-1">
                <Users className="h-3 w-3" /> Family · {allEntries.length}
              </Badge>
              {isPass && <Badge variant="outline">On Pass</Badge>}
            </div>
            <div className="text-xs text-muted-foreground truncate">
              Room {room?.room_number ?? '—'}
            </div>
          </div>
        </div>
        {renderActions(hohEntry)}
      </div>

      <Collapsible open={expanded} onOpenChange={setExpanded}>
        <CollapsibleTrigger className="flex items-center gap-1 text-xs text-muted-foreground mt-2 hover:text-foreground">
          <ChevronDown className={cn('h-3 w-3 transition-transform', expanded && 'rotate-180')} />
          {expanded ? 'Hide members' : 'Mark members individually'}
        </CollapsibleTrigger>
        <CollapsibleContent className="mt-2 space-y-2 pl-6 border-l">
          {memberEntries.map((m) => {
            const c = clientById.get(m.client_id);
            const memberName = c
              ? `${c.first_name ?? ''} ${c.last_name ?? ''}`.trim() || c.name || 'Unknown'
              : 'Unknown';
            return (
              <div
                key={m.id}
                className={cn('flex gap-3 py-1', isMobile ? 'flex-col' : 'items-center')}
              >
                <div className="flex-1 text-sm">{memberName}</div>
                {renderActions(m)}
              </div>
            );
          })}
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
};
